import { ThemeProvider, StyledEngineProvider, useTheme } from "@mui/material";
import React, { useContext, useEffect } from "react";
import reactDom from "react-dom";
import { useSelector } from "react-redux";
import {
  CourseCard,
  CourseCardExpansionPanel,
  CourseCardSummary
} from "../components/CourseCard";
import { courseTypes } from "../constants";
import ExpandedAssignmentsContext from "../context/ExpandedAssignmentsContext";
import UserAssignmentContext from "../context/UserAssignmentContext";
import { makeGetEntities } from "../redux/selectors/selectors";
import coursePic from "../assets/images/course_pic.png";
import CourseCardButtons from "../components/CourseCard/CourseCardButtons";

const selector = makeGetEntities();

export const usePolicyPlayer = content => {
  const theme = useTheme();
  const {
    isAssignmentExpanded,
    alwaysShouldBeExpanded,
    onExpandCourseCard,
    getExpansionPanelItems,
    expandedAssignments
  } = useContext(ExpandedAssignmentsContext);

  const {
    isStarted,
    onCreateUserAssignment,
    onDeleteUserAssignment,
    onStartAssignment,
    onResumeAssignment,
    onChangeEnrollment,
    setOpenCoursesInNewTab
  } = useContext(UserAssignmentContext);

  const { versions, userAssignments } = useSelector(
    ({ userAssignmentsListState }) => ({
      versions: selector(userAssignmentsListState, {
        type: "version"
      }),
      userAssignments: selector(userAssignmentsListState, {
        type: "userAssignment"
      })
    })
  );

  useEffect(() => {
    // This ensures that courses that are opened from policy player are opened in a new tab
    setOpenCoursesInNewTab(true);
    return () => {
      setOpenCoursesInNewTab(false);
    };
  }, []);

  useEffect(() => {
    if (content) {
      const elements = document.querySelectorAll(
        '[data-widget-type="training"]'
      );

      Array.from(elements).forEach(element => {
        const trainingObjectId = element.getAttribute(
          "data-training-object-id"
        );

        let trainingObject = versions.find(({ id }) => id == trainingObjectId);

        if (trainingObject) {
          let userAssignment = trainingObject.userAssignment;

          const courseCard = (
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <CourseCard
                  key={trainingObject.id}
                  buttonsVariant="narrow"
                  scheduledDate={trainingObject.scheduledDate}
                  scheduledTime={trainingObject.scheduledTime}
                  endDate={trainingObject.endDate}
                  endTime={trainingObject.endTime}
                  flags={trainingObject.flags}
                  title={trainingObject.name}
                  postScore={trainingObject.userAssignment?.postScore}
                  passingPostScore={trainingObject.passingScore}
                  status={userAssignment?.status}
                  failedStatus={userAssignment?.failedStatus}
                  courseType={trainingObject.typeName}
                  typeName={trainingObject.typeName}
                  imageSrc={
                    trainingObject.image ? trainingObject.image : coursePic
                  }
                  courseDescription={trainingObject.description}
                  passFail={userAssignment?.passFail}
                  absent={userAssignment?.absent}
                  number={trainingObject.number}
                  manualName={trainingObject.manualName}
                  activitiesSummary={
                    userAssignment?.totalActivities > 0 &&
                    userAssignment?.enrolledCtoaId && (
                      <CourseCardSummary
                        expanded={isAssignmentExpanded(userAssignment)}
                        onActivitiesExpandedChange={() =>
                          onExpandCourseCard(
                            !isAssignmentExpanded(userAssignment),
                            userAssignment
                          )
                        }
                        totalActivities={userAssignment?.totalActivities}
                        numActivitiesCompleted={
                          userAssignment?.numActivitiesCompleted
                        }
                        showProgress={
                          userAssignment?.courseType !== courseTypes.policy
                        }
                      />
                    )
                  }
                  buttons={
                    <CourseCardButtons
                      assignmentType={userAssignment?.assignmentType}
                      courseType={trainingObject.courseType}
                      hasActiveVersions={trainingObject.versions?.length > 0}
                      isAssigned={!!userAssignment?.assignedTrainingObjectId}
                      isEnrolled={!!userAssignment?.enrolledCtoaId}
                      isInstructorForCourse={
                        trainingObject.isInstructorForCourse
                      }
                      isStarted={isStarted(userAssignment)}
                      onAddToCoursesClick={() =>
                        onCreateUserAssignment(trainingObject)
                      }
                      onChangeEnrollmentClick={() =>
                        onChangeEnrollment(userAssignment)
                      }
                      onDeleteUserAssignment={() =>
                        onDeleteUserAssignment(userAssignment)
                      }
                      onEnrollClick={() => onStartAssignment(userAssignment)}
                      onResumeCourseClick={() =>
                        onResumeAssignment(userAssignment)
                      }
                      prereqs={trainingObject.prerequisites || []}
                      startDate={trainingObject.zonedStartDate}
                      endDate={trainingObject.zonedEndDate}
                      iltClassType={trainingObject.typeName}
                    />
                  }
                  expansionPanel={
                    <CourseCardExpansionPanel
                      items={getExpansionPanelItems(userAssignment)}
                      expanded={
                        isAssignmentExpanded(userAssignment) ||
                        alwaysShouldBeExpanded(userAssignment)
                      }
                      passingPostScore={userAssignment?.passingPostScore}
                      description={userAssignment?.content}
                      assignmentRequirements={userAssignment?.requirements}
                      iltInfo={userAssignment?.iltInfo}
                      assignmentType={userAssignment?.typeName}
                    />
                  }
                />
              </ThemeProvider>
            </StyledEngineProvider>
          );

          reactDom.render(courseCard, element);
        }
      });
    }
  }, [content, versions, userAssignments, expandedAssignments]);
};
